import React from "react";
import {
  Box,
  Container,
  Grid,
  Image,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  Heading,
  Badge,
  useColorModeValue,
  Divider,
  List,
  ListItem,
  ListIcon,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import {
  FiShoppingBag,
  FiStar,
  FiCheckCircle,
  FiBattery,
  FiZap,
  FiShield,
  FiArrowLeft,
} from "react-icons/fi";
import { 
  FaMagic, 
  FaHeart, 
  FaShieldAlt,
  FaLeaf,
  FaGem,
  FaRegClock
} from "react-icons/fa";
import type { Product } from "../types";
import { getProductById } from "../data/products";

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const product = getProductById(id || "");

  const handleAddToCart = () => {
    if (!product) {
      toast({
        title: "Error",
        description: "Product not found",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    addToCart(product);
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  if (!product) {
    return (
      <Container maxW="1200px" py={8}>
        <VStack spacing={4} align="center">
          <Text fontSize="xl" color="gray.500">Product not found</Text>
          <Button colorScheme="green" onClick={() => navigate("/products")}>
            Return to Products
          </Button>
        </VStack>
      </Container>
    );
  }

  const isInStock = Number(product.stock) > 0;

  return (
    <Container maxW="1400px" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Back Button */}
        <HStack>
          <IconButton
            aria-label="Go back"
            icon={<FiArrowLeft />}
            onClick={() => navigate(-1)}
            variant="ghost"
          />
          <Text>Back to Products</Text>
        </HStack>

        {/* Product Details */}
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={8}>
          {/* Product Image */}
          <Box position="relative">
            <Image
              src={product.image}
              alt={product.name}
              borderRadius="lg"
              width="100%"
              height="auto"
              objectFit="cover"
            />
            <Badge
              position="absolute"
              top={4}
              right={4}
              colorScheme="blue"
              fontSize="md"
              px={3}
              py={1}
            >
              {product.discount}% OFF
            </Badge>
          </Box>

          {/* Product Info */}
          <VStack align="start" spacing={6}>
            <VStack align="start" spacing={2}>
              <Heading size="xl">{product.name}</Heading>
              <HStack>
                <Icon as={FiStar} color="yellow.400" />
                <Text>{product.rating} / 5.0</Text>
                <Text color="gray.500">({product.sold} sold)</Text>
              </HStack>
            </VStack>

            <Divider />

            <VStack align="start" spacing={2}>
              <HStack>
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  Rs. {product.price.toLocaleString()}
                </Text>
                {product.originalPrice && (
                  <Text
                    fontSize="lg"
                    textDecoration="line-through"
                    color="green.300"
                  >
                    Rs. {product.originalPrice.toLocaleString()}
                  </Text>
                )}
              </HStack>
              <Text color="gray.600">{product.description}</Text>
            </VStack>

            <Divider />

            <VStack align="start" spacing={4}>
              <Text fontWeight="bold" color="green.500" fontSize="lg">Key Features</Text>
              <List spacing={3}>
                {product.features.map((feature, index) => (
                  <ListItem key={index}>
                    <ListIcon as={FaMagic} color="green.400" />
                    <Text as="span" color="gray.700">{feature}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>

            <Divider />

            <VStack align="start" spacing={2}>
              <Text fontWeight="bold">Stock Status</Text>
              <Text color={isInStock ? "green.500" : "red.500"}>
                {isInStock ? "In Stock" : "Out of Stock"}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {product.stock} units available
              </Text>
            </VStack>

            <Button
              bg="green.500"
              color="white"
              size="lg"
              leftIcon={<FiShoppingBag />}
              onClick={handleAddToCart}
              isDisabled={!isInStock}
              _hover={{
                bg: "green.600",
                transform: "translateY(-3px)",
                shadow: "xl",
                boxShadow: "0 4px 20px rgba(236, 72, 153, 0.6)",
              }}
              _active={{
                transform: "translateY(0)",
                boxShadow: "0 2px 10px rgba(236, 72, 153, 0.4)",
              }}
              borderRadius="full"
              px={10}
              py={6}
              fontWeight="bold"
              letterSpacing="wider"
              fontSize="lg"
              transition="all 0.2s"
              border="2px solid"
              borderColor="green.400"
            >
              Add to Cart
            </Button>
          </VStack>
        </Grid>
      </VStack>
    </Container>
  );
};

export default ProductDetail;
